/**
 * MobileNavContext — global on/off state for the mobile slide-in nav drawer.
 *
 * Wired by the App shell. Used by:
 *   • Navigation.js   — applies `translate-x-0/-translate-x-full` based on `open`
 *   • TopBar.js       — renders a hamburger button on mobile that flips `open`
 *   • Route changes   — auto-close on navigation so the drawer doesn't linger
 *
 * Keep this dumb: just a controlled boolean + ergonomic helpers.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const MobileNavCtx = createContext({ open: false, toggle: () => {}, close: () => {} });

export function MobileNavProvider({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Auto-close on every route change so the drawer doesn't sit on top
  // of the new page after the user picks a destination.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open so the underlying page
  // doesn't scroll-bleed behind the overlay.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <MobileNavCtx.Provider value={{ open, toggle, close }}>
      {children}
    </MobileNavCtx.Provider>
  );
}

export function useMobileNav() {
  return useContext(MobileNavCtx);
}
